/**
 * Sheet types + helpers — sheet DATA now lives on each Project entry
 * in `projects.ts`. This module just defines the SheetInfo shape and
 * a couple of project-aware lookup helpers used by stores that can't
 * call Preact hooks.
 *
 * Why the per-project move: the demo now supports multiple model
 * bundles (rac-basic for Snowdon Towers, casita-blanca for the real
 * LA ADU). Their sheet sets and default-sheet routing don't overlap,
 * so the engine + viewer + picker all key off the active Project
 * rather than a global SHEETS constant.
 */

import { useProjectStore } from "../stores/projectStore";

export interface SheetInfo {
  /** Stable id used by the picker, viewer, and rule preferredSheetId. */
  id: string;
  /** Display name on the title block / picker chip. */
  name: string;
  /** Absolute path (from web root) to the SVF/F2D entry file. */
  documentPath: string;
  kind: "2d" | "3d";
}

/**
 * Resolve a sheet by id within the current project. Throws on
 * unknown id — callers should only reference sheets they got from
 * the same project's sheet list.
 */
export function getSheet(id: string): SheetInfo {
  const project = useProjectStore.getState().getCurrentProject();
  const s = project.sheets.find(s => s.id === id);
  if (!s) {
    throw new Error(`Unknown sheet id "${id}" in project ${project.id}`);
  }
  return s;
}

/**
 * 3D-kind sheets behave as universal context for every violation
 * (every dbId is selectable in 3D), so the violations panel's
 * sheet-switch logic skips switching when the user is already in
 * 3D. Caveat: Rooms have no 3D geometry so the highlight may have
 * nothing to fit on.
 */
export function isUniversalSheet(id: string | undefined): boolean {
  if (!id) return false;
  const project = useProjectStore.getState().getCurrentProject();
  return project.sheets.find(s => s.id === id)?.kind === "3d";
}

/** Convenience accessor for components that need the full list. */
export function useSheets(): SheetInfo[] {
  return useProjectStore(s => s.getCurrentProject().sheets);
}
